import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { FileSpreadsheet, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { WagePayoutsDialog, prevMonth } from './WagePayoutsDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const coversMonth = (range, month) => {
  const first = `${month}-01`;
  const last = `${month}-31`;
  return (!range?.startDate || range.startDate <= last) && (!range?.endDate || range.endDate >= first);
};

/** Cost workbook download; warns first when last month's wage payout dates are still blank. */
export const WorkbookButton = ({ projectId, dateRange, all = false }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState(null);
  const [fillOpen, setFillOpen] = useState(false);
  const month = prevMonth();
  const download = async () => {
    setMissing(null);
    setBusy(true);
    try {
      const params = {};
      if (dateRange?.startDate) params.start_date = dateRange.startDate;
      if (dateRange?.endDate) params.end_date = dateRange.endDate;
      const path = all ? 'cost-workbook/all-projects' : `projects/${projectId}/cost-workbook`;
      const r = await axios.get(`${API}/${path}`, { params, responseType: 'blob' });
      const name = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'project_costs.xlsx';
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement('a'), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error(t('budget.workbookFailed'));
    } finally {
      setBusy(false);
    }
  };
  const go = async () => {
    if (coversMonth(dateRange, month)) {
      try {
        const r = await axios.get(`${API}/wage-payouts`, { params: { month } });
        const blank = r.data.rows.filter((x) => x.hours > 0 && !x.paid_date);
        if (blank.length) { setMissing(blank); return; }
      } catch { /* the reminder is optional; download anyway */ }
    }
    download();
  };
  const testId = all ? 'cost-workbook-all-projects' : `cost-workbook-${projectId}`;
  return (
    <>
      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={go} disabled={busy} data-testid={testId}>
        <FileSpreadsheet className="h-3.5 w-3.5 mr-1" />{busy ? t('budget.workbookBuilding') : t(all ? 'budget.workbookAll' : 'budget.workbook')}
      </Button>
      <Dialog open={!!missing} onOpenChange={(o) => !o && setMissing(null)}>
        <DialogContent className="max-w-md" data-testid="payout-reminder-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" />{t('wagePayouts.reminderTitle', { month })}</DialogTitle>
            <DialogDescription>{t('wagePayouts.reminderText', { count: missing?.length || 0 })}</DialogDescription>
          </DialogHeader>
          <p className="text-xs text-slate-600" data-testid="payout-reminder-names">{(missing || []).slice(0, 8).map((x) => x.name).join(', ')}{(missing?.length || 0) > 8 ? '…' : ''}</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={download} data-testid="payout-reminder-download">{t('wagePayouts.downloadAnyway')}</Button>
            <Button onClick={() => { setMissing(null); setFillOpen(true); }} data-testid="payout-reminder-fill">{t('wagePayouts.fillNow')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {fillOpen && <WagePayoutsDialog hideTrigger open={fillOpen} onOpenChange={setFillOpen} initialMonth={month} />}
    </>
  );
};

export default WorkbookButton;
